import type { ReactNode } from "react";
import { DecimalInput } from "../../../DecimalInput";
import { normalizeSamples } from "../utils";
import { HorizontalScrollArea } from "./HorizontalScrollArea";
import { displayResultValue } from "./shared";

type NormalizedSample = ReturnType<typeof normalizeSamples>[number];

export interface SampleValueColumn {
  key: string;
  header: ReactNode;
  /** decimal: comma-decimal input, text: free text input, calculated: read-only value. */
  kind: "decimal" | "text" | "calculated";
  getValue: (sampleId: string) => string | undefined;
  onChange?: (sampleId: string, value: string) => void;
}

const MIN_VALUE_COLUMNS = 9;
const cellInputClassName = "h-[37px] w-[76px] max-w-[calc(100%_-_12px)] min-w-0 rounded-none border border-primary-dark/50 dark:border-primary-light/50 !bg-black/0 px-3 text-center text-[14px] font-bold text-oi-button-blue dark:text-oi-powder-form-yellow outline-none placeholder:text-oi-button-blue dark:placeholder:text-oi-powder-form-yellow focus:border-oi-button-blue dark:focus:border-oi-powder-form-yellow";

/** One row per sample, one column per measurement; shared by every per-sample result table so they look and scroll the same. */
export function SampleValueTable({ scrollAreaId, normalizedSamples, columns, readOnly = false }: { scrollAreaId: string; normalizedSamples: NormalizedSample[]; columns: SampleValueColumn[]; readOnly?: boolean }) {
  const emptyColumns = Array.from({ length: Math.max(0, MIN_VALUE_COLUMNS - columns.length) }, (_, index) => index);

  return (
    <HorizontalScrollArea scrollAreaId={scrollAreaId}>
      <table className="w-full min-w-[800px] table-fixed border-collapse bg-primary-dark/5 dark:bg-black/20 text-center text-[14px] text-primary-dark dark:text-primary-light [&_th]:border-2 [&_th]:border-primary-dark/10 dark:[&_th]:border-oi-dark-background [&_th]:font-normal [&_td]:border-2 [&_td]:border-primary-dark/10 dark:[&_td]:border-oi-dark-background">
        <colgroup>
          <col className="w-[160px]" />
          {Array.from({ length: Math.max(MIN_VALUE_COLUMNS, columns.length) }, (_, index) => <col key={index} />)}
        </colgroup>
        <thead className="h-[50px] bg-primary-dark/5 dark:bg-black/20">
          <tr>
            <th>Sample</th>
            {columns.map(column => <th key={column.key}>{column.header}</th>)}
            {emptyColumns.map(index => <th key={index} aria-hidden="true" />)}
          </tr>
        </thead>
        <tbody>
          {normalizedSamples.map(sample => <tr key={sample.sampleId} className="h-[50px]">
            <td className="whitespace-nowrap px-2.5 text-left">{sample.sampleId}</td>
            {columns.map(column => {
              const ariaLabel = `${sample.sampleId} ${column.key}`;
              const value = column.getValue(sample.sampleId) ?? "";
              return <td key={column.key}>
                {column.kind === "calculated"
                  ? <span aria-label={ariaLabel} className="font-bold text-oi-button-blue dark:text-oi-powder-form-yellow">{displayResultValue(value)}</span>
                  : column.kind === "decimal"
                    ? <DecimalInput disabled={readOnly} aria-label={ariaLabel} className={cellInputClassName} placeholder="-" value={value} onValueChange={next => column.onChange?.(sample.sampleId, next)} />
                    : <input disabled={readOnly} aria-label={ariaLabel} className={cellInputClassName} placeholder="-" value={value} onChange={event => column.onChange?.(sample.sampleId, event.target.value)} />}
              </td>;
            })}
            {emptyColumns.map(index => <td key={index} aria-hidden="true" />)}
          </tr>)}
        </tbody>
      </table>
    </HorizontalScrollArea>
  );
}
